// QuotesPage — Enquiries & Quotes with line-item quoting via /api/quotes (internal only)
import { useCallback, useEffect, useRef, useState } from "react";
import {
    Search, Plus, X, Save, Loader2, FileCheck2, Trash2, Plus as PlusIcon,
    Upload, Download, FileText, FileDown, Printer, Mail, Send, Eye,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Pill from "@/components/Pill";
import { useSortableRows, SortableTh } from "@/lib/sortable";

const ENQUIRY_STATUSES = ["New", "Quoted", "Accepted", "Declined", "Closed"];
const QUOTE_STATUSES = ["Not quoted", "Draft", "Sent", "Accepted", "Declined", "Expired"];
const SOURCES = ["Referral", "Website", "Existing client", "Tender", "Cold outreach", "Other"];
const STATUS_TONE = { New: "blue", Quoted: "amber", Accepted: "green", Declined: "red", Closed: "neutral" };
const QUOTE_TONE = { "Not quoted": "neutral", Draft: "neutral", Sent: "amber", Accepted: "green", Declined: "red", Expired: "red" };
const CAN_EDIT_ROLES = new Set(["director", "administrator"]);

const gbp = (n) => `£${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

let _lineUid = 0;
const newLineUid = () => `li-${++_lineUid}`;

export default function QuotesPage() {
    const [rows, setRows] = useState([]);
    const [clients, setClients] = useState([]);
    const [serviceTypes, setServiceTypes] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("All");
    const [dialog, setDialog] = useState({ open: false, mode: "create", initial: null });
    const [denied, setDenied] = useState(false);
    const { user } = useAuth();
    const canEdit = user && CAN_EDIT_ROLES.has(user.role);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/quotes");
            setRows(data);
            setDenied(false);
        } catch (e) {
            if (e.response?.status === 403) setDenied(true);
        }
    }, []);

    useEffect(() => { api.get("/clients").then((r) => setClients(r.data)).catch(() => {}); }, []);
    useEffect(() => { api.get("/quote-service-types").then((r) => setServiceTypes(r.data)).catch(() => setServiceTypes([])); }, []);
    useEffect(() => { api.get("/fee-proposal-templates").then((r) => setTemplates(r.data)).catch(() => setTemplates([])); }, []);
    useEffect(() => { refresh(); }, [refresh]);

    const filtered = rows.filter((e) => {
        if (statusFilter !== "All" && e.status !== statusFilter) return false;
        if (q && !`${e.company_name} ${e.reference} ${e.contact_name || ""} ${e.service_type || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
        return true;
    });
    const { sorted, sortKey, sortDir, toggleSort } = useSortableRows(filtered, {
        accessors: {
            reference: (r) => r.reference,
            company: (r) => r.company_name,
            service: (r) => r.service_type,
            status: (r) => r.status,
            quote: (r) => r.quote_status,
            total: (r) => r.total_gbp,
        },
        defaultKey: "reference", defaultDir: "desc",
    });

    const pipelineValue = rows.filter((e) => ["New", "Quoted"].includes(e.status)).reduce((a, e) => a + (e.total_gbp || 0), 0);

    return (
        <div data-testid="quotes-page" className="space-y-5">
            {denied && (
                <div data-testid="quotes-denied" className="bg-white border rounded-[11px] p-8 text-center" style={{ borderColor: "#E5E0DA" }}>
                    <p className="text-[14px] font-semibold text-[#1F1F1F]">You don't have access to Enquiries & Quotes.</p>
                    <p className="text-[12.5px] mt-1" style={{ color: "#6F6A64" }}>This module is for Priority Risk internal staff only.</p>
                </div>
            )}
            {!denied && (
            <>
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase"
                          style={{ borderBottom: "2px solid var(--brand-accent, #C75A1B)", color: "#6F6A64" }}>
                        Clients & Portals
                    </span>
                    <h2 className="text-2xl font-bold tracking-tight mt-2 text-[#1F1F1F]">Enquiries & Quotes</h2>
                    <p className="text-[13px] mt-1" style={{ color: "#6F6A64" }}>
                        {rows.length} enquiries · {rows.filter((e) => e.status === "New").length} new · <b>{gbp(pipelineValue)}</b> open quote value
                    </p>
                </div>
                {canEdit && (
                    <button data-testid="quotes-new-button"
                        onClick={() => setDialog({ open: true, mode: "create", initial: null })}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] text-[12.5px] font-semibold text-white"
                        style={{ background: "var(--brand-accent, #C75A1B)" }}>
                        <Plus className="w-3.5 h-3.5" /> New enquiry
                    </button>
                )}
            </div>

            <div className="bg-white border rounded-[11px] shadow-brand-sm overflow-hidden" style={{ borderColor: "#E5E0DA" }}>
                <div className="flex items-center gap-3 px-4 py-3 border-b flex-wrap" style={{ borderColor: "#F0EDE8" }}>
                    <div className="flex items-center gap-2 bg-[#F6F4F0] border rounded-[8px] px-3 py-1.5 flex-1 min-w-[220px] max-w-[340px]" style={{ borderColor: "#E5E0DA" }}>
                        <Search className="w-3.5 h-3.5" style={{ color: "#6F6A64" }} />
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by company, ref, contact, service…"
                            className="flex-1 bg-transparent outline-none text-[12.5px]" data-testid="quotes-search" />
                    </div>
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="quotes-status-filter"
                        className="bg-white border rounded-[8px] px-2 py-1.5 text-[12px] font-semibold outline-none" style={{ borderColor: "#E5E0DA" }}>
                        <option>All</option>{ENQUIRY_STATUSES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                    <div className="ml-auto text-[11.5px]" style={{ color: "#6F6A64" }}>{sorted.length} shown · {rows.length} total</div>
                </div>

                <table className="w-full text-[12.7px]">
                    <thead>
                        <tr className="bg-[#F6F4F0] text-[10.5px] uppercase tracking-wider" style={{ color: "#6F6A64" }}>
                            <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="reference" onClick={toggleSort} className="px-5">Reference</SortableTh>
                            <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="company" onClick={toggleSort}>Company</SortableTh>
                            <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="service" onClick={toggleSort}>Service</SortableTh>
                            <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="status" onClick={toggleSort}>Status</SortableTh>
                            <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="quote" onClick={toggleSort}>Quote</SortableTh>
                            <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="total" onClick={toggleSort}>Total</SortableTh>
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map((e) => (
                            <tr key={e.id} data-testid={`enquiry-row-${e.id}`}
                                onClick={() => setDialog({ open: true, mode: "edit", initial: e })}
                                className="border-t hover:bg-[#FCFBF9] cursor-pointer" style={{ borderColor: "#F0EDE8" }}>
                                <td className="px-5 py-3 font-mono text-[11.5px]" style={{ color: "#9A968F" }}>{e.reference}</td>
                                <td className="px-3 py-3">
                                    <div className="font-semibold text-[#1F1F1F]">{e.company_name}</div>
                                    {e.contact_name && <div className="text-[10.5px]" style={{ color: "#9A968F" }}>{e.contact_name}</div>}
                                </td>
                                <td className="px-3 py-3 text-[#2D2D2D]">{e.service_type || "—"}</td>
                                <td className="px-3 py-3"><Pill tone={STATUS_TONE[e.status] || "neutral"}>{e.status}</Pill></td>
                                <td className="px-3 py-3"><Pill tone={QUOTE_TONE[e.quote_status] || "neutral"}>{e.quote_status}</Pill></td>
                                <td className="px-3 py-3 font-mono text-[#2D2D2D]">{e.total_gbp ? gbp(e.total_gbp) : "—"}</td>
                            </tr>
                        ))}
                        {sorted.length === 0 && (
                            <tr><td colSpan={6} className="px-5 py-10 text-center italic" style={{ color: "#9A968F" }}>
                                <FileCheck2 className="w-5 h-5 mx-auto mb-2 opacity-60" />No enquiries match the filters.
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <EnquiryDialog open={dialog.open} mode={dialog.mode} initial={dialog.initial} canEdit={canEdit}
                clients={clients} serviceTypes={serviceTypes} templates={templates}
                onClose={() => setDialog((d) => ({ ...d, open: false }))} onSaved={refresh} />
            </>
            )}
        </div>
    );
}

function EnquiryDialog({ open, mode = "create", initial, clients, serviceTypes = [], templates = [], canEdit = true, onClose, onSaved }) {
    const [form, setForm] = useState({});
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(false);
    const [savedId, setSavedId] = useState(null);
    const [savedRef, setSavedRef] = useState(null);
    const [docs, setDocs] = useState([]);
    const [uploadingDoc, setUploadingDoc] = useState(false);
    const [docBusyId, setDocBusyId] = useState(null);
    const [selectedTemplateId, setSelectedTemplateId] = useState("");
    const [templateBusy, setTemplateBusy] = useState(false);
    const [exportBusy, setExportBusy] = useState(false);
    const [emailOpen, setEmailOpen] = useState(false);
    const [emailTo, setEmailTo] = useState("");
    const [emailSubject, setEmailSubject] = useState("");
    const [emailMessage, setEmailMessage] = useState("");
    const [emailBusy, setEmailBusy] = useState(false);
    const fileRef = useRef(null);

    const entityId = initial?.id || savedId;
    const reference = initial?.reference || savedRef;

    useEffect(() => {
        setForm(initial || {
            company_name: "", client_id: "", contact_name: "", contact_email: "", contact_phone: "",
            service_type: "", description: "", source: "", owner_name: "", status: "New",
            quote_status: "Not quoted", vat_rate: 20, quote_notes: "", quote_valid_until: "", notes: "",
            include_standard_terms: true,
        });
        setItems(initial?.line_items?.length ? initial.line_items.map((li) => ({ ...li, _uid: newLineUid() })) : []);
        setDocs(initial?.fee_proposal_documents || []);
        setSavedId(null); setSavedRef(null); setEmailOpen(false); setSelectedTemplateId("");
    }, [initial, open]);

    if (!open) return null;
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const vatRate = Number(form.vat_rate ?? 20);
    const subtotal = items.reduce((a, li) => a + (Number(li.quantity) || 0) * (Number(li.unit_price_gbp) || 0), 0);
    const vat = subtotal * vatRate / 100;
    const total = subtotal + vat;

    function setItem(i, k, v) { setItems((arr) => arr.map((li, idx) => idx === i ? { ...li, [k]: v } : li)); }
    function addItem() { setItems((arr) => [...arr, { description: "", quantity: 1, unit_price_gbp: 0, _uid: newLineUid() }]); }
    function removeItem(i) { setItems((arr) => arr.filter((_, idx) => idx !== i)); }

    async function save() {
        if (!form.company_name?.trim()) { toast.error("Company name is required"); return null; }
        if (!form.description?.trim()) { toast.error("Description is required"); return null; }
        const cleaned = Object.fromEntries(
            Object.entries(form).filter(([, v]) => !(typeof v === "string" && v.trim() === ""))
        );
        cleaned.vat_rate = vatRate;
        cleaned.include_standard_terms = form.include_standard_terms !== false;
        cleaned.line_items = items
            .filter((li) => li.description?.trim())
            .map((li) => ({ description: li.description, quantity: Number(li.quantity) || 0, unit_price_gbp: Number(li.unit_price_gbp) || 0 }));
        setBusy(true);
        try {
            if (!entityId) {
                const { data } = await api.post("/quotes", cleaned);
                toast.success(`Created ${data.reference} — you can now attach fee proposal documents or email it below.`);
                setSavedId(data.id); setSavedRef(data.reference); setDocs(data.fee_proposal_documents || []);
                onSaved?.();
                return data.id;
            } else {
                const { data } = await api.patch(`/quotes/${entityId}`, cleaned);
                toast.success(`Updated ${data.reference}`);
                onSaved?.(); onClose?.();
                return entityId;
            }
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
            return null;
        } finally { setBusy(false); }
    }

    async function remove() {
        if (!window.confirm("Delete this enquiry?")) return;
        setBusy(true);
        try {
            await api.delete(`/quotes/${entityId}`);
            toast.success("Enquiry deleted");
            onSaved?.(); onClose?.();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
        } finally { setBusy(false); }
    }

    function openFilePicker() { fileRef.current?.click(); }

    async function onPickFile(e) {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        let id = entityId;
        if (!id) {
            id = await save(); // auto-creates the enquiry from the current form first
            if (!id) return;   // save() already toasted the validation error
        }
        const fd = new FormData();
        fd.append("file", file);
        setUploadingDoc(true);
        try {
            const { data } = await api.post(`/quotes/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            setDocs(data.fee_proposal_documents || []);
            toast.success("Document uploaded");
            onSaved?.();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
        } finally { setUploadingDoc(false); }
    }

    async function previewDoc(d) {
        setDocBusyId(d.id);
        try {
            const res = await api.get(`/quotes/${entityId}/documents/${d.id}/file`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            window.open(url, "_blank");
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (err) { toast.error("Could not preview document"); }
        finally { setDocBusyId(null); }
    }

    async function downloadDoc(d) {
        setDocBusyId(d.id);
        try {
            const res = await api.get(`/quotes/${entityId}/documents/${d.id}/file`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url; a.download = d.filename || "document";
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) { toast.error("Could not download document"); }
        finally { setDocBusyId(null); }
    }

    async function deleteDoc(d) {
        if (!window.confirm(`Remove "${d.filename}"?`)) return;
        setDocBusyId(d.id);
        try {
            const { data } = await api.delete(`/quotes/${entityId}/documents/${d.id}`);
            setDocs(data.fee_proposal_documents || []);
            toast.success("Document removed");
            onSaved?.();
        } catch (err) { toast.error("Could not remove document"); }
        finally { setDocBusyId(null); }
    }

    async function downloadTemplate() {
        if (!selectedTemplateId) return;
        setTemplateBusy(true);
        try {
            const res = await api.get(`/fee-proposal-templates/${selectedTemplateId}/file`, { responseType: "blob" });
            const tpl = templates.find((t) => t.id === selectedTemplateId);
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url; a.download = tpl?.filename || "template";
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) { toast.error("Could not download template"); }
        finally { setTemplateBusy(false); }
    }

    async function previewOrPrintPdf() {
        setExportBusy(true);
        try {
            const res = await api.get(`/quotes/${entityId}/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            window.open(url, "_blank");
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (err) { toast.error("Could not generate PDF"); }
        finally { setExportBusy(false); }
    }

    function openEmail() {
        setEmailTo(form.contact_email || "");
        setEmailSubject(`Fee Proposal — ${form.company_name} (${reference || ""})`);
        setEmailMessage(`Hi ${form.contact_name || ""},\n\nPlease find attached our fee proposal ${reference || ""} for ${form.company_name}.\n\nKind regards,`);
        setEmailOpen(true);
    }

    async function sendEmail() {
        setEmailBusy(true);
        try {
            const { data } = await api.post(`/quotes/${entityId}/email`, { to: emailTo || null, subject: emailSubject || null, message: emailMessage || null });
            toast.success(data.status === "sent" ? `Emailed to ${data.to}` : `Queued for ${data.to} (mailbox not yet configured)`);
            setEmailOpen(false);
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not send email"); }
        finally { setEmailBusy(false); }
    }

    return (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 animate-fade-in" style={{ background: "rgba(26,26,26,.45)" }}>
            <div data-testid="enquiry-form-dialog" className="bg-white rounded-[14px] shadow-brand-lg w-full max-w-[720px] max-h-[92vh] flex flex-col border" style={{ borderColor: "#E5E0DA" }}>
                <div className="px-6 pt-5 pb-4 border-b flex items-start gap-3" style={{ borderColor: "#F0EDE8" }}>
                    <div className="w-10 h-10 rounded-[10px] grid place-items-center" style={{ background: "#FBEFE5", color: "#9F4212" }}><FileCheck2 className="w-5 h-5" /></div>
                    <div className="flex-1 min-w-0">
                        <h2 className="font-display text-[19px] font-bold text-[#1F1F1F]">
                            {reference ? `${mode === "create" ? "New enquiry" : (canEdit ? "Edit" : "View")} — ${reference}` : "New enquiry"}
                        </h2>
                        <p className="text-[12px] mt-0.5" style={{ color: "#6F6A64" }}>
                            {!canEdit ? "View only — you can export, print, or download attached documents." : entityId ? "Build the quote, attach documents, then export/print or email it below." : "Capture an enquiry and build a quote with line items. Totals compute automatically."}
                        </p>
                    </div>
                    <button data-testid="enquiry-form-close" onClick={onClose} className="w-9 h-9 rounded-[8px] hover:bg-[#F6F4F0] grid place-items-center"><X className="w-4 h-4" /></button>
                </div>

                <div className="px-6 py-5 flex-1 overflow-y-auto space-y-4 form-pane">
                    <div className="grid sm:grid-cols-2 gap-3">
                        <Field label="Company name" required><input data-testid="enquiry-form-company" disabled={!canEdit} value={form.company_name || ""} onChange={(e) => set("company_name", e.target.value)} className="fi" /></Field>
                        <Field label="Link to existing client (optional)">
                            <select data-testid="enquiry-form-client" disabled={!canEdit} value={form.client_id || ""} onChange={(e) => set("client_id", e.target.value)} className="fi">
                                <option value="">— prospect / not a client —</option>
                                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </Field>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-3">
                        <Field label="Contact name"><input data-testid="enquiry-form-contact" disabled={!canEdit} value={form.contact_name || ""} onChange={(e) => set("contact_name", e.target.value)} className="fi" /></Field>
                        <Field label="Contact email"><input data-testid="enquiry-form-email" disabled={!canEdit} value={form.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} className="fi" /></Field>
                        <Field label="Contact phone"><input data-testid="enquiry-form-phone" disabled={!canEdit} value={form.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} className="fi" /></Field>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-3">
                        <Field label="Service type">
                            <select data-testid="enquiry-form-service" disabled={!canEdit} value={form.service_type || ""} onChange={(e) => set("service_type", e.target.value)} className="fi">
                                <option value="">Select…</option>{serviceTypes.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
                            </select>
                        </Field>
                        <Field label="Source">
                            <select data-testid="enquiry-form-source" disabled={!canEdit} value={form.source || ""} onChange={(e) => set("source", e.target.value)} className="fi">
                                <option value="">Select…</option>{SOURCES.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </Field>
                        <Field label="Owner"><input data-testid="enquiry-form-owner" disabled={!canEdit} value={form.owner_name || ""} onChange={(e) => set("owner_name", e.target.value)} className="fi" /></Field>
                    </div>
                    <Field label="Enquiry description" required><textarea data-testid="enquiry-form-description" disabled={!canEdit} rows={2} value={form.description || ""} onChange={(e) => set("description", e.target.value)} className="fi" /></Field>

                    <div className="grid sm:grid-cols-2 gap-3">
                        <Field label="Enquiry status">
                            <select data-testid="enquiry-form-status" disabled={!canEdit} value={form.status || "New"} onChange={(e) => set("status", e.target.value)} className="fi">
                                {ENQUIRY_STATUSES.map((s) => <option key={s}>{s}</option>)}
                            </select>
                        </Field>
                        <Field label="Quote status">
                            <select data-testid="enquiry-form-quote-status" disabled={!canEdit} value={form.quote_status || "Not quoted"} onChange={(e) => set("quote_status", e.target.value)} className="fi">
                                {QUOTE_STATUSES.map((s) => <option key={s}>{s}</option>)}
                            </select>
                        </Field>
                    </div>

                    {templates.length > 0 && (
                        <div className="border rounded-[10px] p-3 flex items-end gap-2" style={{ borderColor: "#E5E0DA", background: "#FCFBF9" }}>
                            <Field label="Fee proposal template — download a starting point">
                                <select data-testid="enquiry-template-select" value={selectedTemplateId} onChange={(e) => setSelectedTemplateId(e.target.value)} className="fi">
                                    <option value="">Select a template…</option>
                                    {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                                </select>
                            </Field>
                            <button type="button" data-testid="enquiry-template-download" disabled={!selectedTemplateId || templateBusy} onClick={downloadTemplate}
                                className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-3 py-2 rounded-[8px] border bg-white disabled:opacity-50" style={{ borderColor: "#E5E0DA" }}>
                                {templateBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Download
                            </button>
                        </div>
                    )}

                    {/* Line items */}
                    <div className="border rounded-[10px] p-3" style={{ borderColor: "#E5E0DA", background: "#FCFBF9" }}>
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-[12px] font-bold text-[#1F1F1F]">Quote line items</span>
                            {canEdit && (
                                <button type="button" onClick={addItem} data-testid="enquiry-add-line"
                                    className="inline-flex items-center gap-1 text-[11.5px] font-semibold px-2 py-1 rounded-[6px] border bg-white" style={{ borderColor: "#E5E0DA", color: "var(--brand-accent, #C75A1B)" }}>
                                    <PlusIcon className="w-3 h-3" /> Add line
                                </button>
                            )}
                        </div>
                        {items.length === 0 && <div className="text-[11.5px] italic py-2" style={{ color: "#9A968F" }}>No line items yet. Add one to build the quote.</div>}
                        {items.map((li, i) => (
                            <div key={li._uid || i} data-testid={`enquiry-line-${i}`} className={`grid ${canEdit ? "grid-cols-[1fr_70px_90px_90px_28px]" : "grid-cols-[1fr_70px_90px_90px]"} gap-2 items-center mb-2`}>
                                <input data-testid={`enquiry-line-desc-${i}`} disabled={!canEdit} placeholder="Description" value={li.description || ""} onChange={(e) => setItem(i, "description", e.target.value)} className="fi" />
                                <input data-testid={`enquiry-line-qty-${i}`} disabled={!canEdit} type="number" placeholder="Qty" value={li.quantity ?? ""} onChange={(e) => setItem(i, "quantity", e.target.value)} className="fi text-right" />
                                <input data-testid={`enquiry-line-price-${i}`} disabled={!canEdit} type="number" placeholder="Unit £" value={li.unit_price_gbp ?? ""} onChange={(e) => setItem(i, "unit_price_gbp", e.target.value)} className="fi text-right" />
                                <div className="text-[11.5px] font-mono text-right" style={{ color: "#2D2D2D" }}>{gbp((Number(li.quantity) || 0) * (Number(li.unit_price_gbp) || 0))}</div>
                                {canEdit && <button type="button" onClick={() => removeItem(i)} data-testid={`enquiry-line-remove-${i}`} className="w-7 h-7 grid place-items-center rounded-[6px] hover:bg-[#FBE8E5] text-[#B33A2E]"><Trash2 className="w-3.5 h-3.5" /></button>}
                            </div>
                        ))}
                        <div className="grid grid-cols-[1fr_auto] gap-2 mt-3 pt-3 border-t text-[12px]" style={{ borderColor: "#F0EDE8" }}>
                            <div className="text-right" style={{ color: "#6F6A64" }}>Subtotal</div>
                            <div data-testid="enquiry-subtotal" className="text-right font-mono font-semibold w-[110px]">{gbp(subtotal)}</div>
                            <div className="text-right flex items-center justify-end gap-1" style={{ color: "#6F6A64" }}>
                                VAT
                                <input data-testid="enquiry-form-vat" disabled={!canEdit} type="number" value={form.vat_rate ?? 20} onChange={(e) => set("vat_rate", e.target.value)}
                                    className="w-[48px] px-1 py-0.5 border rounded text-right text-[11px]" style={{ borderColor: "#E5E0DA" }} />%
                            </div>
                            <div data-testid="enquiry-vat" className="text-right font-mono font-semibold w-[110px]">{gbp(vat)}</div>
                            <div className="text-right font-bold text-[#1F1F1F]">Total</div>
                            <div data-testid="enquiry-total" className="text-right font-mono font-bold w-[110px]" style={{ color: "var(--brand-accent, #C75A1B)" }}>{gbp(total)}</div>
                        </div>
                        <label className="flex items-center gap-2 text-[11.5px] font-medium mt-3 pt-3 border-t" style={{ borderColor: "#F0EDE8", color: "#2D2D2D" }}>
                            <input data-testid="enquiry-form-include-terms" disabled={!canEdit} type="checkbox" checked={form.include_standard_terms !== false} onChange={(e) => set("include_standard_terms", e.target.checked)} />
                            Include standard Terms &amp; Conditions <span style={{ color: "#9A968F" }}>(managed in Settings — appended automatically to the exported PDF)</span>
                        </label>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-3">
                        <Field label="Quote valid until"><input data-testid="enquiry-form-valid" disabled={!canEdit} type="date" value={form.quote_valid_until || ""} onChange={(e) => set("quote_valid_until", e.target.value)} className="fi" /></Field>
                        <Field label="Quote notes"><input data-testid="enquiry-form-quote-notes" disabled={!canEdit} value={form.quote_notes || ""} onChange={(e) => set("quote_notes", e.target.value)} className="fi" /></Field>
                    </div>
                    <Field label="Internal notes"><textarea data-testid="enquiry-form-notes" disabled={!canEdit} rows={2} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} className="fi" /></Field>

                    {/* Fee proposal documents */}
                    <div className="border rounded-[10px] p-3" style={{ borderColor: "#E5E0DA", background: "#FCFBF9" }}>
                        <span className="text-[12px] font-bold text-[#1F1F1F] block mb-2">Fee proposal documents</span>
                        <div className="space-y-2" data-testid="enquiry-documents-list">
                            {docs.length === 0 && <p className="text-[11.5px] italic" style={{ color: "#9A968F" }}>No documents uploaded yet.</p>}
                            {docs.map((d) => (
                                <div key={d.id} data-testid={`enquiry-document-${d.id}`} className="flex items-center gap-2 bg-white border rounded-[8px] px-2.5 py-1.5" style={{ borderColor: "#E5E0DA" }}>
                                    <FileText className="w-3.5 h-3.5 shrink-0" style={{ color: "#2A5DB0" }} />
                                    <span className="flex-1 text-[11.5px] truncate text-[#1F1F1F]" title={d.filename}>{d.filename}</span>
                                    <button type="button" data-testid={`enquiry-document-preview-${d.id}`} disabled={docBusyId === d.id} onClick={() => previewDoc(d)} title="Preview" className="w-7 h-7 grid place-items-center rounded-[6px] hover:bg-[#F6F4F0]"><Eye className="w-3.5 h-3.5" style={{ color: "#6F6A64" }} /></button>
                                    <button type="button" data-testid={`enquiry-document-download-${d.id}`} disabled={docBusyId === d.id} onClick={() => downloadDoc(d)} title="Download" className="w-7 h-7 grid place-items-center rounded-[6px] hover:bg-[#F6F4F0]"><Download className="w-3.5 h-3.5" style={{ color: "#6F6A64" }} /></button>
                                    {canEdit && <button type="button" data-testid={`enquiry-document-delete-${d.id}`} disabled={docBusyId === d.id} onClick={() => deleteDoc(d)} title="Delete" className="w-7 h-7 grid place-items-center rounded-[6px] hover:bg-[#FBE8E5]"><Trash2 className="w-3.5 h-3.5" style={{ color: "#B33A2E" }} /></button>}
                                </div>
                            ))}
                            {canEdit && (
                                <>
                                    <input ref={fileRef} type="file" hidden onChange={onPickFile} data-testid="enquiry-document-file-input" />
                                    <button type="button" data-testid="enquiry-document-upload" disabled={uploadingDoc} onClick={openFilePicker}
                                        className="w-full inline-flex items-center justify-center gap-1.5 text-[11.5px] font-semibold py-1.5 rounded-[7px] disabled:opacity-60" style={{ color: "#C8632A", background: "#FBEDE6" }}>
                                        {uploadingDoc ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Upload document
                                    </button>
                                    {!entityId && (
                                        <p className="text-[10.5px] italic" style={{ color: "#9A968F" }}>The enquiry will be saved automatically the first time you attach a file.</p>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    {entityId && (
                        <div className="border rounded-[10px] p-3 space-y-2" style={{ borderColor: "#E5E0DA", background: "#FCFBF9" }}>
                            <span className="text-[12px] font-bold text-[#1F1F1F] block">Export, print &amp; email</span>
                            <div className="flex items-center gap-2 flex-wrap">
                                <button type="button" data-testid="enquiry-export-pdf" disabled={exportBusy} onClick={previewOrPrintPdf}
                                    className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-3 py-1.5 rounded-[7px] border bg-white disabled:opacity-60" style={{ borderColor: "#E5E0DA" }}>
                                    {exportBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />} Export PDF
                                </button>
                                <button type="button" data-testid="enquiry-print-pdf" disabled={exportBusy} onClick={previewOrPrintPdf}
                                    className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-3 py-1.5 rounded-[7px] border bg-white disabled:opacity-60" style={{ borderColor: "#E5E0DA" }}>
                                    <Printer className="w-3.5 h-3.5" /> Print
                                </button>
                                {canEdit && !emailOpen && (
                                    <button type="button" data-testid="enquiry-email-open" onClick={openEmail}
                                        className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-3 py-1.5 rounded-[7px] border bg-white" style={{ borderColor: "#E5E0DA" }}>
                                        <Mail className="w-3.5 h-3.5" /> Email proposal
                                    </button>
                                )}
                            </div>
                            {canEdit && emailOpen && (
                                <div className="border rounded-[8px] p-2.5 space-y-2 bg-white" style={{ borderColor: "#E5E0DA" }}>
                                    <input data-testid="enquiry-email-to" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="Recipient email" className="fi" />
                                    <input data-testid="enquiry-email-subject" value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} placeholder="Subject" className="fi" />
                                    <textarea data-testid="enquiry-email-message" rows={3} value={emailMessage} onChange={(e) => setEmailMessage(e.target.value)} placeholder="Message" className="fi" />
                                    <div className="flex items-center gap-2">
                                        <button type="button" data-testid="enquiry-email-send" disabled={emailBusy} onClick={sendEmail}
                                            className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-3 py-1.5 rounded-[7px] text-white disabled:opacity-60" style={{ background: "var(--brand-accent, #C75A1B)" }}>
                                            {emailBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send
                                        </button>
                                        <button type="button" data-testid="enquiry-email-cancel" onClick={() => setEmailOpen(false)} className="text-[11.5px] font-semibold px-3 py-1.5" style={{ color: "#9A968F" }}>Cancel</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="px-6 py-4 border-t flex items-center gap-3" style={{ borderColor: "#F0EDE8" }}>
                    {entityId && canEdit && (
                        <button onClick={remove} disabled={busy} data-testid="enquiry-form-delete"
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[8px] text-[12.5px] font-semibold text-[#B33A2E] hover:bg-[#FBE8E5]"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                    )}
                    <div className="flex-1" />
                    <button onClick={onClose} disabled={busy} data-testid="enquiry-form-cancel" className="px-4 py-2 rounded-[8px] text-[12.5px] font-semibold border bg-white" style={{ borderColor: "#E5E0DA" }}>{!canEdit ? "Close" : (entityId && !initial ? "Done" : "Cancel")}</button>
                    {canEdit && (
                        <button onClick={save} disabled={busy} data-testid="enquiry-form-save"
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-[12.5px] font-semibold text-white" style={{ background: "var(--brand-accent, #C75A1B)" }}>
                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}{busy ? "Saving…" : (entityId ? "Save changes" : "Save")}
                        </button>
                    )}
                </div>
                <style>{`
                    .form-pane .fi { width: 100%; padding: 8px 11px; background: #fff; border: 1px solid #E5E0DA; border-radius: 8px; font-size: 12.5px; color: #1F1F1F; outline: none; transition: border-color .12s, box-shadow .12s; }
                    .form-pane .fi:focus { border-color: var(--brand-accent, #C75A1B); box-shadow: 0 0 0 3px #FBEFE5; }
                    .form-pane .fi:disabled { background: #FAFAF8; color: #6F6A64; cursor: not-allowed; }
                `}</style>
            </div>
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="text-[11px] font-semibold mb-1 block" style={{ color: "#6F6A64" }}>
                {label} {required ? <span className="text-[#B33A2E]">*</span> : null}
            </span>
            {children}
        </label>
    );
}
